import { useState, useMemo } from "react";
import { format, subDays, startOfDay, endOfDay, startOfWeek, startOfMonth, subMonths, parse, isValid } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { CalendarIcon, X, Check } from "lucide-react";

const PRESETS = [
  { label: "Today", value: "today", range: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }) },
  { label: "Yesterday", value: "yesterday", range: () => ({ from: startOfDay(subDays(new Date(), 1)), to: endOfDay(subDays(new Date(), 1)) }) },
  { label: "Last 7 days", value: "7d", range: () => ({ from: startOfDay(subDays(new Date(), 6)), to: endOfDay(new Date()) }) },
  { label: "Last 14 days", value: "14d", range: () => ({ from: startOfDay(subDays(new Date(), 13)), to: endOfDay(new Date()) }) },
  { label: "Last 30 days", value: "30d", range: () => ({ from: startOfDay(subDays(new Date(), 29)), to: endOfDay(new Date()) }) },
  { label: "This week", value: "week", range: () => ({ from: startOfWeek(new Date(), { weekStartsOn: 1 }), to: endOfDay(new Date()) }) },
  { label: "This month", value: "month", range: () => ({ from: startOfMonth(new Date()), to: endOfDay(new Date()) }) },
  { label: "Last month", value: "last_month", range: () => { const s = startOfMonth(subMonths(new Date(), 1)); return { from: s, to: endOfDay(subDays(startOfMonth(new Date()), 1)) }; } },
];

const DATE_FORMAT = "dd/MM/yyyy";

function parseInputDate(str) {
  if (!str || str.length !== 10) return null;
  const d = parse(str, DATE_FORMAT, new Date());
  return isValid(d) ? d : null;
}

export function DateRangePicker({ dateRange, onDateRangeChange, className = "" }) {
  const [open, setOpen] = useState(false);
  const [staged, setStaged] = useState(undefined);
  const [activePreset, setActivePreset] = useState(null);
  const [fromInput, setFromInput] = useState("");
  const [toInput, setToInput] = useState("");

  const hasRange = Boolean(dateRange?.from);
  const canApply = staged?.from && staged?.to;

  const label = useMemo(() => {
    if (!dateRange?.from) return "Date range";
    if (activePreset) {
      const preset = PRESETS.find((p) => p.value === activePreset);
      if (preset) return preset.label;
    }
    if (dateRange.from && dateRange.to) return `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d, yyyy")}`;
    return "Date range";
  }, [dateRange, activePreset]);

  const handleOpen = (isOpen) => {
    if (isOpen) {
      setStaged(dateRange);
      setFromInput(dateRange?.from ? format(dateRange.from, DATE_FORMAT) : "");
      setToInput(dateRange?.to ? format(dateRange.to, DATE_FORMAT) : "");
    }
    setOpen(isOpen);
  };

  const handlePreset = (preset) => {
    setActivePreset(preset.value);
    onDateRangeChange(preset.range());
    setOpen(false);
  };

  const handleCalendarSelect = (range) => {
    setStaged(range);
    setActivePreset(null);
    if (range?.from) setFromInput(format(range.from, DATE_FORMAT));
    if (range?.to) setToInput(format(range.to, DATE_FORMAT));
    else setToInput("");
  };

  const handleFromInputChange = (val) => {
    setFromInput(val);
    const d = parseInputDate(val);
    if (d) {
      setStaged((prev) => ({ from: d, to: prev?.to || undefined }));
      setActivePreset(null);
    }
  };

  const handleToInputChange = (val) => {
    setToInput(val);
    const d = parseInputDate(val);
    if (d) {
      setStaged((prev) => ({ from: prev?.from || undefined, to: d }));
      setActivePreset(null);
    }
  };

  const handleApply = () => {
    if (canApply) {
      onDateRangeChange({ from: startOfDay(staged.from), to: endOfDay(staged.to) });
      setActivePreset(null);
      setOpen(false);
    }
  };

  const handleClear = (e) => {
    if (e) e.stopPropagation();
    onDateRangeChange(undefined);
    setStaged(undefined);
    setActivePreset(null);
    setFromInput("");
    setToInput("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={`rounded-sm text-xs h-8 gap-1.5 font-normal ${hasRange ? "border-[#003DA5] text-[#003DA5]" : "text-[#52525B]"} ${className}`} data-testid="date-range-trigger">
          <CalendarIcon size={13} />
          <span className="truncate max-w-[180px]">{label}</span>
          {hasRange && <span onClick={handleClear} className="ml-0.5 hover:text-red-500" data-testid="date-range-clear"><X size={12} /></span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end" sideOffset={4}>
        <div className="flex" data-testid="date-range-popover">
          <PresetPanel activePreset={activePreset} onPreset={handlePreset} hasRange={hasRange} onClear={handleClear} />
          <div className="p-2">
            <DateInputFields
              fromInput={fromInput} toInput={toInput}
              onFromChange={handleFromInputChange} onToChange={handleToInputChange}
            />
            <Calendar mode="range" selected={staged} onSelect={handleCalendarSelect} numberOfMonths={2} data-testid="date-range-calendar" />
            <Separator className="mb-2" />
            <div className="flex items-center justify-end px-1">
              <Button size="sm" disabled={!canApply} onClick={handleApply} className="rounded-sm bg-[#003DA5] hover:bg-[#002D7A] text-white text-xs h-7 gap-1" data-testid="date-range-apply">
                <Check size={12} /> Apply
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DateInputFields({ fromInput, toInput, onFromChange, onToChange }) {
  return (
    <div className="flex items-end gap-3 px-1 pb-2">
      <div className="flex-1 space-y-1">
        <Label className="text-[10px] tracking-[0.12em] uppercase font-bold text-[#52525B]">From Date</Label>
        <Input
          type="text"
          placeholder="DD/MM/YYYY"
          value={fromInput}
          onChange={(e) => onFromChange(e.target.value)}
          className="h-8 text-xs rounded-sm border-zinc-300 focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
          data-testid="date-from-input"
        />
      </div>
      <span className="text-xs text-[#71717A] pb-1.5">to</span>
      <div className="flex-1 space-y-1">
        <Label className="text-[10px] tracking-[0.12em] uppercase font-bold text-[#52525B]">To Date</Label>
        <Input
          type="text"
          placeholder="DD/MM/YYYY"
          value={toInput}
          onChange={(e) => onToChange(e.target.value)}
          className="h-8 text-xs rounded-sm border-zinc-300 focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
          data-testid="date-to-input"
        />
      </div>
    </div>
  );
}

function PresetPanel({ activePreset, onPreset, hasRange, onClear }) {
  return (
    <div className="w-[140px] border-r border-[#E4E4E7] p-2 space-y-0.5">
      {PRESETS.map((preset) => (
        <button key={preset.value} onClick={() => onPreset(preset)}
          className={`w-full text-left px-2.5 py-1.5 text-xs rounded-sm transition-colors ${activePreset === preset.value ? "bg-[#003DA5] text-white" : "text-[#09090B] hover:bg-[#F4F4F5]"}`}
          data-testid={`preset-${preset.value}`}>
          {preset.label}
        </button>
      ))}
      <Separator className="my-1.5" />
      {hasRange && (
        <button onClick={onClear} className="w-full text-left px-2.5 py-1.5 text-xs rounded-sm text-red-600 hover:bg-red-50 transition-colors" data-testid="preset-clear">
          Clear filter
        </button>
      )}
    </div>
  );
}
